import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DesktopSidebar from './DesktopSidebar';
import DesktopTopNav from './DesktopTopNav';
import DesktopAppBar from './DesktopAppBar';
import { desktopTheme as t } from '../styles/desktopTheme';

const LayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${t.bg};
  color: ${t.textPrimary};
  /* Used by DesktopAppBar (fixed) to align with sidebar width */
  --sidebar-width: ${p => p.$sidebarWidth};
`;

const SidebarWrapper = styled.div`
  width: ${p => p.$sidebarWidth};
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  transition: width 200ms ease;
`;

const MainArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100vh;
  overflow: hidden;
`;

const ContentArea = styled.main`
  flex: 1;
  overflow-y: ${p => (p.$scroll === 'hidden' ? 'hidden' : 'auto')};
  overflow-x: hidden;
  padding: 24px 32px;
  padding-bottom: 120px; /* Space for app bar */
  display: flex;
  justify-content: center;
  min-height: 0;
  width: 100%;
  align-items: stretch;
`;

/**
 * Desktop layout wrapper with sidebar, top nav, and app bar
 * Used for viewport >= 1024px
 */
export default function DesktopLayout({ children, activeApp }) {
  const storageKey = 'dizee_desktop_sidebar_collapsed';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) setSidebarCollapsed(saved === 'true');
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a slim rail when collapsed so the toggle remains "inside the sidebar"
  const sidebarWidth = useMemo(() => (sidebarCollapsed ? '64px' : '320px'), [sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch (e) {
        // ignore
      }
      return next;
    });
  };

  return (
    <LayoutContainer $sidebarWidth={sidebarWidth}>
      <SidebarWrapper $sidebarWidth={sidebarWidth}>
        <DesktopSidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
      </SidebarWrapper>
      <MainArea>
        <DesktopTopNav activeApp={activeApp} />
        <ContentArea $scroll={activeApp === 'chat' || activeApp === 'timeline' ? 'hidden' : 'auto'}>
          {children}
        </ContentArea>
        <DesktopAppBar />
      </MainArea>
    </LayoutContainer>
  );
}

